import Modal from 'react-modal'
import { Link } from 'react-router-dom'
import { AdminPartneredHotspotDto } from '@ivy-monorepo/shared'
import { BaseModalProps } from '../../types/modal'
import { formatTicketPrice } from '../../utils/ticketPrice'
import { formatJourneyDate } from '../../utils/eventJourney'
import EventStatTiles from '../EventStatTiles'

type PartneredHotspotDetailModalProps = BaseModalProps & {
  hotspot: AdminPartneredHotspotDto | null
  status: string
  onEdit: (hotspot: AdminPartneredHotspotDto) => void
  onDelete: (hotspot: AdminPartneredHotspotDto) => void
}

/** A partnered hotspot's window, its event, and the hotspot funnel (modal → CTA → RSVP / tickets). */
const PartneredHotspotDetailModal = ({ isOpen, onClose, hotspot, status, onEdit, onDelete }: PartneredHotspotDetailModalProps) => {
  if (!hotspot) return null
  const { stats } = hotspot

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      className="modal-popup event-detail-modal"
      overlayClassName="modal-overlay"
      contentLabel="Partnered hotspot"
    >
      <div className="modal-content-wrapper">
        <div className="modal-body-content">
          <div className="event-detail-modal__title">
            <h3>{hotspot.event.name}</h3>
            {hotspot.eventIsTicketed && (
              <span className="event-ticketed-badge" title="Ticketed (paid) event">
                Ticket · {formatTicketPrice(hotspot.eventTicketPriceCents)}
              </span>
            )}
          </div>
          <p className="event-detail-modal__subtitle">
            <Link to={`/admin/venues/${hotspot.venue.publicId}`}>{hotspot.venue.name}</Link>
            {hotspot.venue.address ? ` · ${hotspot.venue.address}` : ''}
          </p>

          <section className="event-journey-section">
            <h4>Details</h4>
            <dl>
              <div>
                <dt>Status</dt>
                <dd>{status}</dd>
              </div>
              <div>
                <dt>Partnered</dt>
                <dd>
                  {formatJourneyDate(hotspot.startsAt)} → {formatJourneyDate(hotspot.endsAt)}
                </dd>
              </div>
              <div>
                <dt>Event dates</dt>
                <dd>
                  {formatJourneyDate(hotspot.eventStartDate)} → {formatJourneyDate(hotspot.eventEndDate)}
                </dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{formatJourneyDate(hotspot.createdAt)}</dd>
              </div>
            </dl>
          </section>

          <section className="event-journey-section">
            <h4>Hotspot funnel</h4>
            <EventStatTiles
              tiles={[
                { label: 'Modal auto-opened', value: stats.modalShown, users: stats.modalShownUsers },
                { label: 'Card taps', value: stats.cardTaps, users: stats.cardTapUsers },
                { label: 'CTA taps', value: stats.ctaTaps, users: stats.ctaUsers, ofUsers: stats.modalShownUsers },
                { label: 'RSVPs from hotspot', value: stats.hotspotRsvps },
                { label: 'All RSVPs to the event', value: stats.totalRsvps },
                ...(hotspot.eventIsTicketed
                  ? [
                      { label: '"Get a Ticket" taps from hotspot', value: stats.hotspotGetTicketsTaps },
                      { label: 'Tickets from hotspot', value: stats.hotspotTickets },
                    ]
                  : []),
              ]}
            />
            <p className="event-journey-empty">
              Taps count every occurrence (unique users underneath). RSVPs and tickets count those made from the hotspot&apos;s CTA.
            </p>
          </section>
        </div>

        <div className="modal-footer-content">
          <Link className="styled-button event-detail-modal__link" to={`/admin/venue-events/${hotspot.event.publicId}/journey`} onClick={onClose}>
            View user journeys
          </Link>
          <button type="button" className="styled-button" onClick={() => onDelete(hotspot)}>
            Delete
          </button>
          <button type="button" className="styled-button" onClick={() => onEdit(hotspot)}>
            Edit
          </button>
          <button type="button" className="styled-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default PartneredHotspotDetailModal
